<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: '900px' }"
    :title="modalTitle"
    @close="$emit('close')"
  >
    <div class="row full-width">
      <VersionControlBar
        v-if="versions.length"
        class="full-width"
        :versions="versions"
      />
    </div>

    <QForm
      class="column full-width"
      @submit="saveRule"
    >
      <QueryEditor
        v-model="rule.boolean_query"
        class="full-width q-mb-md"
        hide-instant-load-results
        label="Boolean Query"
        :media-types="rule.qualityControlTrigger.enabled_media_types"
        show-errors
      />
      <QInput
        v-model="rule.note"
        class="q-mb-sm"
        filled
        label="Note"
        outlined
        square
        type="textarea"
      />
      <QSelect
        v-model="rule.reaction"
        filled
        label="Reaction"
        :options="reactionOptions"
        :rules="[(val) => (val && val.length) || 'Must select a reaction']"
      />

      <div class="q-mt-sm flex">
        <InputDatePicker
          v-model="expireAt"
          clearable
          date-style="short"
          :min-date="new Date()"
          placeholder="Rule expiry time"
          show-date-input
          show-time-input
          time-style="short"
        />
      </div>

      <QToggle
        v-model="rule.applyAll"
        color="blue-5"
        icon="update"
        keep-color
        label="Apply to all pending reviews"
        style="max-width: 250px"
      />

      <QCardActions align="right">
        <QBtn
          class="q-mx-sm"
          color="red"
          label="Archive Rule"
          @click="archiveRule"
        />
        <QBtn
          class="q-mx-sm"
          color="warning"
          label="Cancel"
          @click="cancel"
        />
        <QBtn
          class="q-mx-sm"
          color="green"
          label="Save"
          type="submit"
        />
      </QCardActions>
    </QForm>
  </Modal>
</template>

<script>
import { InputDatePicker } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { QueryEditor } from "shared/components/QueryEditor";
import useModal from "shared/composables/useModal";

import VersionControlBar from "src/components/VersionControlBar.vue";

export default {
  name: "RuleEditorModal",
  components: {
    Modal,
    QueryEditor,
    VersionControlBar,
    InputDatePicker,
  },
  props: {
    rule: {
      type: Object,
      required: true,
    },
  },
  emits: ["ruleUpdated", "close"],
  setup() {
    const { close } = useModal("RuleEditorModal");

    return { close };
  },
  data() {
    return {
      versions: [],
      reactionOptions: ["Approve", "Reject for organisation"],
    };
  },
  computed: {
    expireAt: {
      get() {
        if (this.rule.expire_at) {
          return new Date(this.rule.expire_at);
        }

        return null;
      },
      set(value) {
        this.rule.expire_at = value;
      },
    },
    modalTitle() {
      if (this.rule.id) {
        return `Edit Trigger Rule: ${this.rule.id}`;
      }

      return "New Trigger Rule";
    },
  },
  mounted() {
    this.loadVersions();
  },
  methods: {
    async loadVersions() {
      try {
        if (this.rule.id) {
          this.versions = (
            await this.$streemApiAdmin.get(
              `quality_control_rules/${this.rule.id}`,
              {
                transformKeys: true,
              }
            )
          ).data.versions;

          this.versions.sort(
            (first, second) =>
              Date.parse(second.createdAt) - Date.parse(first.createdAt)
          );
        }
      } catch (error) {
        this.$error("Error loading versions", error);
      }
    },
    async saveRule() {
      try {
        const params = {
          boolean_query: this.rule.boolean_query,
          note: this.rule.note,
          quality_control_trigger_id: this.rule.qualityControlTrigger.id,
          reaction: this.rule.reaction,
          apply_all: this.rule.applyAll,
          expire_at: this.expireAt,
        };

        if (this.rule.id) {
          await this.$streemApiAdmin.put(
            `quality_control_rules/${this.rule.id}`,
            { params }
          );
        } else {
          await this.$streemApiAdmin.post("quality_control_rules", { params });
        }

        this.$emit("ruleUpdated");
        this.close();
      } catch (error) {
        this.$error("Error saving rule", error);
      }
    },
    async archiveRule() {
      await this.$streemApiAdmin.put(
        `quality_control_rules/${this.rule.id}/archive`
      );

      this.close();
    },
    cancel() {
      this.close();
    },
  },
};
</script>
